/**
 * Shared authored-schema field controls — used by AuthorEvidenceDialog
 * and CoverageRequirementPanel inline entry.
 */
import React from 'react';
import type { AuthoredFieldDef } from '../../../types/authoredEvidence';

const AuthoredFieldShell: React.FC<{
  fieldKey: string;
  labelId: string;
  label: string;
  unit?: string;
  required?: boolean;
  compact?: boolean;
  children: React.ReactNode;
}> = ({ fieldKey, labelId, label, unit, required, compact, children }) => (
  <div
    className={compact ? 'space-y-0.5 min-w-0' : 'space-y-1'}
    data-testid={`author-evidence-field-${fieldKey}`}
  >
    {!compact && (
      <label
        htmlFor={labelId}
        className="type-section text-text-secondary flex items-center gap-1"
      >
        {label}
        {unit && <span className="text-text-muted normal-case font-normal">({unit})</span>}
        {required && <span className="text-danger" aria-label="required">*</span>}
      </label>
    )}
    {children}
  </div>
);

export const AuthoredFieldInput: React.FC<{
  field: AuthoredFieldDef;
  value: string | number | boolean | undefined;
  onChange: (value: string | number | boolean) => void;
  /** Exact control ID when an inline parent supplies its external label. */
  inputId?: string;
  /** Compact layout for inline panel rows (label rendered externally). */
  compact?: boolean;
}> = ({ field, value, onChange, inputId, compact = false }) => {
  const instanceId = React.useId();
  // A dialog can remain mounted beside the same inline schema field.
  // Labels must target this instance, never the other editor's control.
  const labelId = inputId ?? `${instanceId}-author-evidence-field-${field.key}`;
  const stringValue = value === undefined || value === null ? '' : String(value);

  const inputClass = compact
    ? 'w-full text-[10px] border border-border bg-background px-1.5 py-0.5 rounded-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono'
    : 'w-full text-[11px] border border-border bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary';

  const shellProps = {
    fieldKey: field.key,
    labelId,
    label: field.label,
    unit: field.unit,
    required: field.required,
    compact,
  };

  switch (field.type) {
    case 'number':
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <input
            id={labelId}
            type="number"
            inputMode="decimal"
            step="any"
            className={inputClass}
            placeholder={field.placeholder ?? (field.unit ? `e.g. 200 ${field.unit}` : undefined)}
            value={stringValue}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onChange('');
                return;
              }
              const n = Number(raw);
              if (Number.isFinite(n)) onChange(n);
              else onChange(raw);
            }}
            data-testid={`coverage-inline-field-input-${field.key}`}
          />
        </AuthoredFieldShell>
      );
    case 'date':
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <input
            id={labelId}
            type="date"
            className={inputClass}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`coverage-inline-field-input-${field.key}`}
          />
        </AuthoredFieldShell>
      );
    case 'enum':
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <select
            id={labelId}
            className={inputClass}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`coverage-inline-field-input-${field.key}`}
          >
            <option value="">— select —</option>
            {(field.enumValues ?? []).map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </AuthoredFieldShell>
      );
    case 'textarea':
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <textarea
            id={labelId}
            className={`${inputClass} min-h-[60px]`}
            placeholder={field.placeholder}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`coverage-inline-field-input-${field.key}`}
          />
        </AuthoredFieldShell>
      );
    case 'boolean':
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <label className="inline-flex items-center gap-2 text-[11px] text-text-primary">
            <input
              id={labelId}
              type="checkbox"
              className="w-3.5 h-3.5"
              checked={value === true}
              onChange={(e) => onChange(e.target.checked)}
              data-testid={`coverage-inline-field-input-${field.key}`}
            />
            <span>{field.placeholder ?? 'Yes'}</span>
          </label>
        </AuthoredFieldShell>
      );
    case 'list':
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <input
            id={labelId}
            type="text"
            className={inputClass}
            placeholder={field.placeholder ?? 'Comma-separated values'}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`coverage-inline-field-input-${field.key}`}
          />
        </AuthoredFieldShell>
      );
    case 'string':
    default:
      return (
        <AuthoredFieldShell {...shellProps}>
          {!compact && field.help && <p className="type-body text-text-muted">{field.help}</p>}
          <input
            id={labelId}
            type="text"
            className={inputClass}
            placeholder={field.placeholder}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`coverage-inline-field-input-${field.key}`}
          />
        </AuthoredFieldShell>
      );
  }
};
